import { useState, useCallback, useRef, useMemo } from "react"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
  PhoneIncomingIcon,
  PhoneOutgoingIcon,
  UserIcon,
  CheckIcon,
  ChevronsUpDownIcon,
  XIcon,
  PlusIcon,
} from "lucide-react"
import type { Contact } from "./types"

type RecipientPickerProps = {
  toNumbers: string[]
  popoverOpen: boolean
  setPopoverOpen: React.Dispatch<React.SetStateAction<boolean>>
  setToNumbers: React.Dispatch<React.SetStateAction<string[]>>
  contacts: Contact[]
  prevRecNumbers: string[]
  prevSentNumbers: string[]
}

export function RecipientPicker({
  toNumbers,
  setToNumbers,
  contacts,
  prevRecNumbers,
  prevSentNumbers,
  popoverOpen,
  setPopoverOpen,
}: RecipientPickerProps) {
  const [searchQuery, setSearchQuery] = useState("")
  const [manualInput, setManualInput] = useState("")
  const containerRef = useRef<HTMLDivElement>(null)
  const searchInputRef = useRef<HTMLInputElement>(null)
  const manualInputRef = useRef<HTMLInputElement>(null)

  const hasContacts = contacts.length > 0
  const hasReceivedNumbers = prevRecNumbers.length > 0
  const hasSentNumbers = prevSentNumbers.length > 0

  // Filter items based on search query
  const filteredContacts = useMemo(() => {
    if (!searchQuery) return contacts
    const query = searchQuery.toLowerCase()
    return contacts.filter(
      (c) =>
        c.name.toLowerCase().includes(query) ||
        c.number.toLowerCase().includes(query)
    )
  }, [contacts, searchQuery])

  const filteredRecNumbers = useMemo(() => {
    if (!searchQuery) return prevRecNumbers
    return prevRecNumbers.filter((n) => n.includes(searchQuery))
  }, [prevRecNumbers, searchQuery])

  const filteredSentNumbers = useMemo(() => {
    if (!searchQuery) return prevSentNumbers
    return prevSentNumbers.filter((n) => n.includes(searchQuery))
  }, [prevSentNumbers, searchQuery])

  const hasResults =
    filteredContacts.length > 0 ||
    filteredRecNumbers.length > 0 ||
    filteredSentNumbers.length > 0

  const handleToggle = useCallback(
    (value: string) => {
      setToNumbers((prev) =>
        prev.includes(value)
          ? prev.filter((n) => n !== value)
          : [...prev, value]
      )
    },
    [setToNumbers]
  )

  const handleRemove = useCallback(
    (value: string) => {
      setToNumbers((prev) => prev.filter((n) => n !== value))
    },
    [setToNumbers]
  )

  const handleAddManual = useCallback(() => {
    const trimmed = manualInput.trim()
    if (trimmed && !toNumbers.includes(trimmed)) {
      setToNumbers((prev) => [...prev, trimmed])
      setManualInput("")
      manualInputRef.current?.focus()
    }
  }, [manualInput, toNumbers, setToNumbers])

  const handleOpenChange = (open: boolean) => {
    setPopoverOpen(open)
    if (open) {
      setTimeout(() => searchInputRef.current?.focus(), 0)
    } else {
      setSearchQuery("")
    }
  }

  return (
    <div className="space-y-2">
      <Label htmlFor="to-field">To</Label>
      <div className="relative" ref={containerRef}>
        <Button
          id="to-field"
          variant="outline"
          role="button"
          onClick={() => handleOpenChange(!popoverOpen)}
          className="w-full justify-between font-normal h-auto min-h-9 py-1.5"
        >
          {toNumbers.length > 0 ? (
            <span className="text-sm truncate">
              {toNumbers.length} recipient
              {toNumbers.length !== 1 ? "s" : ""} selected
            </span>
          ) : (
            <span className="text-muted-foreground">
              Search or enter numbers...
            </span>
          )}
          <ChevronsUpDownIcon
            className={`ml-2 size-4 shrink-0 opacity-50 transition-transform ${
              popoverOpen ? "rotate-180" : ""
            }`}
          />
        </Button>

        {/* Dropdown content */}
        {popoverOpen && (
          <>
            {/* Backdrop */}
            <div
              className="fixed inset-0 z-40"
              onClick={() => handleOpenChange(false)}
            />

            {/* Dropdown panel */}
            <div className="absolute top-full left-0 right-0 z-50 mt-1 border rounded-md bg-popover shadow-md">
              {/* Search input */}
              <div className="border-b p-2 sticky top-0 bg-popover">
                <Input
                  ref={searchInputRef}
                  type="text"
                  placeholder="Search numbers or contacts..."
                  className="h-8 text-sm"
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                />
              </div>

              {/* Results */}
              <div className="max-h-64 overflow-y-auto">
                {!hasResults ? (
                  <div className="flex flex-col items-center gap-1.5 py-4 px-2">
                    <p className="text-muted-foreground text-sm">
                      No matches found.
                    </p>
                    <p className="text-muted-foreground text-xs">
                      Type a number below to add manually.
                    </p>
                  </div>
                ) : (
                  <>
                    {/* Contacts */}
                    {filteredContacts.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                          Contacts
                        </div>
                        {filteredContacts.map((contact) => (
                          <button
                            key={`contact-${contact.number}`}
                            onClick={() => handleToggle(contact.number)}
                            className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2 group"
                          >
                            <UserIcon className="text-amber-500 size-4 flex-shrink-0" />
                            <div className="flex flex-col flex-1 min-w-0">
                              <span className="text-sm">{contact.name}</span>
                              <span className="text-muted-foreground font-mono text-xs">
                                {contact.number}
                              </span>
                            </div>
                            {toNumbers.includes(contact.number) && (
                              <CheckIcon className="size-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Divider */}
                    {filteredContacts.length > 0 &&
                      (filteredRecNumbers.length > 0 ||
                        filteredSentNumbers.length > 0) && (
                        <div className="border-t" />
                      )}

                    {/* Received From */}
                    {filteredRecNumbers.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                          Received From
                        </div>
                        {filteredRecNumbers.map((number) => (
                          <button
                            key={`rec-${number}`}
                            onClick={() => handleToggle(number)}
                            className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2"
                          >
                            <PhoneIncomingIcon className="text-emerald-500 size-4 flex-shrink-0" />
                            <span className="font-mono text-sm flex-1">
                              {number}
                            </span>
                            {toNumbers.includes(number) && (
                              <CheckIcon className="size-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}

                    {/* Divider */}
                    {filteredRecNumbers.length > 0 &&
                      filteredSentNumbers.length > 0 && (
                        <div className="border-t" />
                      )}

                    {/* Sent To */}
                    {filteredSentNumbers.length > 0 && (
                      <div>
                        <div className="px-3 py-2 text-xs font-semibold text-muted-foreground bg-muted/50">
                          Sent To
                        </div>
                        {filteredSentNumbers.map((number) => (
                          <button
                            key={`sent-${number}`}
                            onClick={() => handleToggle(number)}
                            className="w-full text-left px-3 py-2 hover:bg-accent flex items-center gap-2"
                          >
                            <PhoneOutgoingIcon className="text-blue-500 size-4 flex-shrink-0" />
                            <span className="font-mono text-sm flex-1">
                              {number}
                            </span>
                            {toNumbers.includes(number) && (
                              <CheckIcon className="size-4 text-primary flex-shrink-0" />
                            )}
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {/* Manual entry */}
              <div className="border-t px-3 py-2 bg-popover">
                <p className="text-muted-foreground text-xs mb-1.5">
                  Or type a number directly
                </p>
                <div className="flex items-center gap-2">
                  <Input
                    ref={manualInputRef}
                    type="text"
                    placeholder="+15552223333"
                    className="h-8 text-sm"
                    value={manualInput}
                    onChange={(e) => setManualInput(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter") {
                        e.preventDefault()
                        handleAddManual()
                      }
                    }}
                  />
                  <Button
                    type="button"
                    size="sm"
                    variant="secondary"
                    className="h-8 px-2.5"
                    disabled={!manualInput.trim()}
                    onClick={handleAddManual}
                  >
                    <PlusIcon className="size-3.5 mr-1" />
                    Add
                  </Button>
                </div>
              </div>
            </div>
          </>
        )}
      </div>

      {/* Selected recipients as badges */}
      {toNumbers.length > 0 && (
        <div className="flex flex-wrap items-center gap-1.5">
          {toNumbers.map((num) => {
            const contact = contacts.find((c) => c.number === num)
            return (
              <Badge
                key={num}
                variant="secondary"
                className="gap-1 font-mono text-xs"
              >
                {contact ? `${contact.name} (${num})` : num}
                <button
                  type="button"
                  className="ml-0.5 rounded-full hover:bg-foreground/10"
                  onClick={() => handleRemove(num)}
                  aria-label={`Remove ${num}`}
                >
                  <XIcon className="size-3" />
                </button>
              </Badge>
            )
          })}
        </div>
      )}
    </div>
  )
}
